import SolutionsCaseStudyCTA from '@web/sections/Solutions/CaseStudyCTA'
import SolutionsChallenges from '@web/sections/Solutions/Challenges'
import SolutionsFooter from '@web/sections/Solutions/Footer'
import SolutionsHeader from '@web/sections/Solutions/Header'
import OtherSectors from '@web/sections/Solutions/OtherSectors'
import SolutionsRegulatoryContext from '@web/sections/Solutions/RegulatoryContext'
import SolutionsUseCases from '@web/sections/Solutions/UseCases'

export function AdaptationPlanningPage() {
  return (
    <>
      <SolutionsHeader
        header="Adaptation planning through Resilience Explorer®"
        subheader="Guide communities through changing climate risk."
        arcadeLink={{
          src: 'https://demo.arcade.software/rwp0tcNflT8uxaFe2wqA?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
          title: 'How Resilience Explorer® Enables Adaptation Planning',
        }}
        descriptionLeft={
          <p>
            Resilience Explorer® brings your climate and natural hazard risk evidence together in
            one place. It helps you understand how risks affect infrastructure and communities,
            explore how different responses compare, and build the foundation for planning decisions
            that can be explained, defended, and updated as conditions change.
          </p>
        }
        descriptionRight={
          <p>
            Long-term decisions about how communities respond to climate and natural hazard risks
            are among the most consequential you will make. The evidence behind them needs to be
            credible, current, and clear enough to bring elected members, communities, and partner
            organisations with you.
          </p>
        }
      />
      <SolutionsChallenges
        header="Challenges facing Adaptation Planning"
        sections={[
          {
            title:
              'Making long-term decisions while future climate and natural hazard risks remain uncertain',
            listitems: [],
          },
          {
            title:
              'Building agreement across communities with different values, priorities, and risk tolerance',
            listitems: [],
          },
          {
            title: 'Ensuring decisions are supported by evidence that withstands scrutiny',
            listitems: [],
          },
        ]}
      />
      <SolutionsRegulatoryContext text="National adaptation policy now expects councils to understand climate and natural hazard risk and plan how communities will respond. Resilience Explorer® helps you do both." />
      <SolutionsUseCases
        header="How Resilience Explorer® Enables Adaptation Planning"
        image={{
          src: '/website/adaptation_planning.jpg',
          alt: 'Adaptation Planning Header',
        }}
        accordions={[
          {
            title: 'Build a shared, updateable evidence base',
            subtitle:
              'Work from one consistent source of risk information across your organisation.',
            content: (
              <>
                <p>
                  When different teams work from different reports prepared at different times,
                  alignment is difficult and gaps emerge.
                </p>
                <p className="mt-4">
                  Resilience Explorer® gives your organisation a single, continuously updated
                  evidence base that adaptation, infrastructure, land use, emergency management, and
                  strategy teams can all work from together.
                </p>
                <p className="mt-4">
                  This means your teams stay aligned, conversations with partners and communities
                  are grounded in the same evidence, and you are never defending a decision with
                  information that has already been superseded.
                </p>
              </>
            ),
          },
          {
            title: 'Understand how impacts spread across systems',
            subtitle:
              'See how disruption in one place can affect services and communities elsewhere.',
            content: (
              <>
                <p>
                  Climate and natural hazard impacts rarely stay contained. A damaged bridge may
                  isolate a community; a disrupted electricity supply may affect water services.
                </p>
                <p className="mt-4">
                  Resilience Explorer® identifies the dependencies between infrastructure, services,
                  and communities, showing where failures could have wider consequences.
                </p>
                <p className="mt-4">
                  This means you can prioritise investment based on real consequence, not just
                  direct exposure, and make the case for it clearly.
                </p>
              </>
            ),
          },
          {
            title: 'Identify when action may be needed',
            subtitle:
              'Understand how risk may change over time and when intervention may be required.',
            content: (
              <>
                <p>
                  Risk does not stay static, and planning horizons of 50 to 100 years make timing
                  decisions genuinely difficult.
                </p>
                <p className="mt-4">
                  Resilience Explorer® lets you explore how exposure and potential impacts may
                  increase under different future scenarios, identifying when assets, services, or
                  communities may face increasing pressure.
                </p>
                <p className="mt-4">
                  This means you can build responses with enough lead time to be effective, rather
                  than reacting once thresholds have already been crossed.
                </p>
              </>
            ),
          },
          {
            title: 'Compare adaptation options before committing resources',
            subtitle: 'Explore different responses and understand their potential effects.',
            content: (
              <>
                <p>
                  Protect, accommodate, or retreat: the choices are rarely straightforward and the
                  stakes are high.
                </p>
                <p className="mt-4">
                  Resilience Explorer® lets you compare options side by side using a consistent
                  evidence base, examining how different actions may influence future impacts.
                </p>
                <p className="mt-4">
                  This means you can make the case for your chosen pathway with confidence, and
                  revisit it as conditions change.
                </p>
              </>
            ),
          },
          {
            title: 'Communicate risk and trade-offs clearly',
            subtitle:
              'Help decision makers and communities understand what climate and natural hazard risk means in practice.',
            content: (
              <>
                <p>
                  Technical risk information is only useful if people can engage with it, and
                  building shared understanding across very different audiences is one of the
                  hardest parts of adaptation planning.
                </p>
                <p className="mt-4">
                  Resilience Explorer® presents risk through maps, summaries, and structured outputs
                  designed to support clear discussion across technical teams, governance, and
                  communities.
                </p>
                <p className="mt-4">
                  This means you can explain what is at risk, why it matters, and what choices are
                  available, without requiring your audience to interpret complex datasets
                  themselves.
                </p>
              </>
            ),
          },
          {
            title: 'Support Long Term Plans and funding decisions',
            subtitle: 'Provide evidence that supports planning and investment decisions.',
            content: (
              <>
                <p>
                  Adaptation decisions need to be justified through formal planning and funding
                  processes, and the evidence behind them needs to hold up over time.
                </p>
                <p className="mt-4">
                  Resilience Explorer® generates structured outputs that support Long Term Plans,
                  adaptation strategies, consultation processes, and funding applications.
                </p>
                <p className="mt-4">
                  This means you can approach each planning cycle with current, auditable evidence
                  rather than commissioning a new assessment from scratch.
                </p>
              </>
            ),
          },
        ]}
      />
      <SolutionsCaseStudyCTA header="Build adaptation strategies your organisation can explain, defend, and deliver as climate and natural hazard risk evolves." />
      <OtherSectors />
      <SolutionsFooter />
    </>
  )
}
